import { EPSILON, clamp, gt, gte, lt, lte, quantize } from '../time.js';
import { itemEnd as itemEndOf, sequenceDuration } from '../query.js';
import { assertEditable, carveRange, cloneItem, cloneTrack, insertOverwrite, insertRipple, minDuration, normalizeTrack, replaceTracks, requireItem, requireTrack, shiftItem, shiftItemsAfter, snapshotTracks, sortItems, targetTracks, } from './helpers.js';
import { CommandError, cmd, } from './types.js';
const MIN_SPEED = 0.1;
const MAX_SPEED = 10;
export function applyCommand(seq, command, options = {}) {
    const now = options.now ?? (() => new Date().toISOString());
    const ctx = {
        seq,
        command,
        idFactory: options.idFactory ?? deterministicIds(command),
        now,
        idOverride: options.idFactory,
    };
    const result = dispatch(ctx);
    return {
        sequence: { ...result.sequence, revision: seq.revision + 1 },
        inverse: result.inverse,
        changed: result.changed,
    };
}
/** Aplica uma lista de comandos em sequência. Erro em qualquer um aborta tudo. */
export function applyCommands(seq, commands, options = {}) {
    let current = seq;
    const inverses = [];
    const changed = new Set();
    for (const c of commands) {
        const r = applyCommand(current, c, options);
        current = r.sequence;
        inverses.unshift(r.inverse); // desfazer na ordem reversa
        r.changed.forEach((id) => changed.add(id));
    }
    return { sequence: current, inverses, changed: [...changed] };
}
/**
 * Ids derivados do id do comando.
 *
 * Alguns comandos criam itens implicitamente (o lado direito de um corte dentro de um
 * DELETE_RANGE, por exemplo). Se esses ids fossem aleatórios, reaplicar o mesmo log
 * produziria uma timeline diferente — e o replay é a base do versionamento e da
 * colaboração. Derivar do id do comando torna o redo bit-a-bit idêntico ao original.
 */
function deterministicIds(command) {
    const seed = command.id.replace(/^cmd_/, '');
    let n = 0;
    return () => `itm_${seed}_${++n}`;
}
/** Cada comando (inclusive dentro de um BATCH) recebe sua própria fábrica determinística. */
function dispatchScoped(ctx) {
    return dispatch({ ...ctx, idFactory: ctx.idOverride ?? deterministicIds(ctx.command) });
}
function dispatch(ctx) {
    const { command } = ctx;
    switch (command.type) {
        case 'ADD_TRACK':
            return addTrack(ctx, command.payload);
        case 'REMOVE_TRACK':
            return removeTrack(ctx, command.payload);
        case 'UPDATE_TRACK':
            return updateTrack(ctx, command.payload);
        case 'REORDER_TRACKS':
            return reorderTracks(ctx, command.payload);
        case 'ADD_ITEM':
            return addItem(ctx, command.payload);
        case 'REMOVE_ITEMS':
            return removeItems(ctx, command.payload);
        case 'UPDATE_ITEMS':
            return updateItems(ctx, command.payload);
        case 'MOVE_ITEMS':
            return moveItems(ctx, command.payload);
        case 'TRIM_ITEM':
            return trimItem(ctx, command.payload);
        case 'SPLIT_ITEM':
            return splitItem(ctx, command.payload);
        case 'DUPLICATE_ITEMS':
            return duplicateItems(ctx, command.payload);
        case 'DELETE_RANGE':
            return rangeOp(ctx, command.payload, true);
        case 'LIFT_RANGE':
            return rangeOp(ctx, command.payload, false);
        case 'INSERT_GAP':
            return insertGap(ctx, command.payload);
        case 'SET_SPEED':
            return setSpeed(ctx, command.payload);
        case 'SET_TRANSFORM':
            return setTransform(ctx, command.payload);
        case 'SET_CROP':
            return setCrop(ctx, command.payload);
        case 'SET_VOLUME':
            return setVolume(ctx, command.payload);
        case 'SET_KEYFRAMES':
            return setKeyframes(ctx, command.payload);
        case 'ADD_KEYFRAME':
            return addKeyframe(ctx, command.payload);
        case 'REMOVE_KEYFRAMES':
            return removeKeyframes(ctx, command.payload);
        case 'UPDATE_KEYFRAME':
            return updateKeyframe(ctx, command.payload);
        case 'ADD_EFFECT':
            return addEffect(ctx, command.payload);
        case 'REMOVE_EFFECT':
            return removeEffect(ctx, command.payload);
        case 'UPDATE_EFFECT':
            return updateEffect(ctx, command.payload);
        case 'ADD_TEXT':
            return addText(ctx, command.payload);
        case 'UPDATE_TEXT':
            return updateText(ctx, command.payload);
        case 'ADD_CAPTIONS':
            return addCaptions(ctx, command.payload);
        case 'UPDATE_CAPTION_STYLE':
            return updateCaptionStyle(ctx, command.payload);
        case 'SET_CAPTION_WORDS':
            return setCaptionWords(ctx, command.payload);
        case 'ADD_MARKER':
            return addMarker(ctx, command.payload);
        case 'REMOVE_MARKER':
            return removeMarker(ctx, command.payload);
        case 'UPDATE_MARKER':
            return updateMarker(ctx, command.payload);
        case 'GROUP_ITEMS':
            return groupItems(ctx, command.payload);
        case 'UNGROUP_ITEMS':
            return ungroupItems(ctx, command.payload);
        case 'SET_SEQUENCE_SETTINGS':
            return setSequenceSettings(ctx, command.payload);
        case 'RESTORE_TRACKS':
            return restoreTracks(ctx, command.payload);
        case 'BATCH':
            return batch(ctx, command.payload);
        default: {
            const never = command;
            throw new CommandError('UNKNOWN_COMMAND', `Comando desconhecido: ${JSON.stringify(never)}`);
        }
    }
}
// ---------------------------------------------------------------------------
// Infra de inverso
// ---------------------------------------------------------------------------
function inv(ctx, type, payload) {
    return cmd({
        type,
        sequenceId: ctx.seq.id,
        payload: payload,
        source: ctx.command.source,
        actorId: ctx.command.actorId,
        changeId: ctx.command.changeId,
        createdAt: ctx.now(),
    });
}
/** Inverso genérico: devolve as tracks ao estado anterior. Sempre correto. */
function restoreInverse(ctx, trackIds, includeMarkers = false) {
    const payload = {
        tracks: snapshotTracks(ctx.seq, trackIds),
    };
    if (includeMarkers)
        payload.markers = ctx.seq.markers.map((m) => ({ ...m }));
    return inv(ctx, 'RESTORE_TRACKS', payload);
}
function normalized(seq, trackIds) {
    const ids = new Set(trackIds);
    return {
        ...seq,
        tracks: seq.tracks.map((t) => (ids.has(t.id) ? normalizeTrack(t, seq.frameRate) : t)),
    };
}
// ---------------------------------------------------------------------------
// Estrutura
// ---------------------------------------------------------------------------
function addTrack(ctx, p) {
    const { seq } = ctx;
    if (seq.tracks.some((t) => t.id === p.track.id)) {
        throw new CommandError('TRACK_EXISTS', `Track ${p.track.id} já existe`);
    }
    const tracks = sortTracks([...seq.tracks, cloneTrack(p.track)]);
    return {
        sequence: { ...seq, tracks },
        inverse: inv(ctx, 'REMOVE_TRACK', { trackId: p.track.id }),
        changed: p.track.items.map((i) => i.id),
    };
}
function removeTrack(ctx, p) {
    const { seq } = ctx;
    const track = requireTrack(seq, p.trackId);
    return {
        sequence: { ...seq, tracks: seq.tracks.filter((t) => t.id !== p.trackId) },
        inverse: inv(ctx, 'ADD_TRACK', { track: cloneTrack(track) }),
        changed: track.items.map((i) => i.id),
    };
}
function updateTrack(ctx, p) {
    const { seq } = ctx;
    const track = requireTrack(seq, p.trackId);
    const before = {};
    for (const key of Object.keys(p.patch)) {
        ;
        before[key] = track[key];
    }
    const next = { ...track, ...p.patch };
    return {
        sequence: replaceTracks(seq, [next]),
        inverse: inv(ctx, 'UPDATE_TRACK', { trackId: p.trackId, patch: before }),
        changed: [],
    };
}
function reorderTracks(ctx, p) {
    const { seq } = ctx;
    const previous = [...seq.tracks].sort((a, b) => a.index - b.index).map((t) => t.id);
    const indexOf = new Map(p.order.map((id, i) => [id, i]));
    const tracks = sortTracks(seq.tracks.map((t) => ({ ...t, index: indexOf.get(t.id) ?? t.index })));
    return {
        sequence: { ...seq, tracks },
        inverse: inv(ctx, 'REORDER_TRACKS', { order: previous }),
        changed: [],
    };
}
function sortTracks(tracks) {
    return [...tracks].sort((a, b) => a.index - b.index || a.id.localeCompare(b.id));
}
// ---------------------------------------------------------------------------
// Itens
// ---------------------------------------------------------------------------
function addItem(ctx, p) {
    const { seq } = ctx;
    const track = requireTrack(seq, p.trackId);
    assertEditable(track);
    const item = quantizeItem(cloneItem(p.item), seq);
    if (item.duration < minDuration(seq.frameRate) - EPSILON) {
        throw new CommandError('ITEM_TOO_SHORT', 'Item menor que um frame');
    }
    const next = p.mode === 'insert'
        ? insertRipple(track, item, seq.frameRate)
        : insertOverwrite(track, item, seq.frameRate, ctx.idFactory);
    return {
        sequence: normalized(replaceTracks(seq, [next]), [track.id]),
        inverse: restoreInverse(ctx, [track.id]),
        changed: [item.id],
    };
}
function removeItems(ctx, p) {
    const { seq } = ctx;
    const targets = new Set(p.itemIds);
    const affected = [];
    const updated = [];
    for (const track of seq.tracks) {
        const hits = track.items.filter((i) => targets.has(i.id));
        if (hits.length === 0)
            continue;
        assertEditable(track);
        affected.push(track.id);
        let next = { ...track, items: track.items.filter((i) => !targets.has(i.id)) };
        if (p.ripple) {
            // Fecha os buracos da direita para a esquerda para não interferir nos offsets.
            for (const hit of [...hits].sort((a, b) => b.start - a.start)) {
                next = shiftItemsAfter(next, hit.start, -hit.duration, seq.frameRate);
            }
        }
        updated.push(next);
    }
    if (updated.length === 0) {
        throw new CommandError('ITEM_NOT_FOUND', 'Nenhum dos itens informados existe');
    }
    return {
        sequence: normalized(replaceTracks(seq, updated), affected),
        inverse: restoreInverse(ctx, affected),
        changed: p.itemIds,
    };
}
function updateItems(ctx, p) {
    const { seq } = ctx;
    const byTrack = new Map();
    const inversePatches = [];
    for (const { itemId, patch } of p.patches) {
        const { track } = requireItem(seq, itemId);
        assertEditable(track);
        const current = byTrack.get(track.id) ?? track;
        const items = current.items.map((item) => {
            if (item.id !== itemId)
                return item;
            const before = {};
            for (const key of Object.keys(patch)) {
                before[key] = item[key];
            }
            inversePatches.push({ itemId, patch: before });
            return quantizeItem({ ...cloneItem(item), ...patch }, seq);
        });
        byTrack.set(track.id, { ...current, items: sortItems(items) });
    }
    const affected = [...byTrack.keys()];
    return {
        sequence: normalized(replaceTracks(seq, [...byTrack.values()]), affected),
        inverse: inv(ctx, 'UPDATE_ITEMS', { patches: inversePatches }),
        changed: p.patches.map((x) => x.itemId),
    };
}
function moveItems(ctx, p) {
    const { seq } = ctx;
    const affected = new Set();
    const moving = [];
    for (const move of p.moves) {
        const { track, item } = requireItem(seq, item_id(move.itemId));
        assertEditable(track);
        const destId = move.trackId ?? track.id;
        const dest = requireTrack(seq, destId);
        assertEditable(dest);
        if (!compatible(dest, item)) {
            throw new CommandError('TRACK_KIND_MISMATCH', `Item ${item.kind} não pode entrar numa track ${dest.kind}`);
        }
        affected.add(track.id);
        affected.add(destId);
        moving.push({ item, from: track.id, to: destId, start: quantize(Math.max(0, move.start), seq.frameRate) });
    }
    // 1) retira todos os itens em movimento das tracks de origem
    const working = new Map();
    for (const id of affected)
        working.set(id, cloneTrack(requireTrack(seq, id)));
    const movingIds = new Set(moving.map((m) => m.item.id));
    for (const [id, track] of working) {
        working.set(id, { ...track, items: track.items.filter((i) => !movingIds.has(i.id)) });
    }
    // 2) reinsere na posição de destino
    for (const m of moving) {
        const dest = working.get(m.to);
        const delta = m.start - m.item.start;
        const placed = shiftItem(cloneItem(m.item), delta, seq.frameRate);
        working.set(m.to, p.mode === 'insert'
            ? insertRipple(dest, placed, seq.frameRate)
            : insertOverwrite(dest, placed, seq.frameRate, ctx.idFactory));
    }
    return {
        sequence: normalized(replaceTracks(seq, [...working.values()]), [...affected]),
        inverse: restoreInverse(ctx, [...affected]),
        changed: [...movingIds],
    };
}
function item_id(id) {
    return id;
}
/** Uma track aceita apenas itens do seu domínio — vídeo em track de áudio é erro. */
/**
 * Um item deste tipo pode viver nesta track?
 *
 * Exportado porque a interface precisa da MESMA resposta que o reducer: arrastar um
 * clip de áudio para cima de uma track de vídeo tem que ser recusado enquanto o
 * ponteiro ainda está no ar, não depois de soltar com um erro vermelho.
 */
export function compatible(track, item) {
    switch (track.kind) {
        case 'video':
        case 'overlay':
            return item.kind === 'video' || item.kind === 'image' || item.kind === 'shape';
        case 'image':
            return item.kind === 'image' || item.kind === 'shape';
        case 'audio':
        case 'music':
        case 'sfx':
            return item.kind === 'audio' || item.kind === 'video';
        case 'text':
            return item.kind === 'text';
        case 'caption':
            return item.kind === 'caption';
        case 'adjustment':
            return item.kind === 'shape';
        default:
            return true;
    }
}
function trimItem(ctx, p) {
    const { seq } = ctx;
    const { track, item } = requireItem(seq, p.itemId);
    assertEditable(track);
    const fps = seq.frameRate;
    const min = minDuration(fps);
    const time = quantize(Math.max(0, p.time), fps);
    const oldStart = item.start;
    const oldEnd = itemEndOf(item);
    let newStart = oldStart;
    let newEnd = oldEnd;
    if (p.edge === 'start') {
        newStart = clamp(time, mediaLowerBound(item), oldEnd - min);
    }
    else {
        newEnd = clamp(time, oldStart + min, mediaUpperBound(item));
    }
    const sliced = sliceItemForTrim(item, newStart, newEnd, fps);
    if (!sliced)
        throw new CommandError('ITEM_TOO_SHORT', 'Trim resultaria em item menor que um frame');
    let items = track.items.map((i) => (i.id === p.itemId ? sliced : i));
    let next = { ...track, items: sortItems(items) };
    if (p.ripple) {
        const delta = p.edge === 'start' ? oldStart - newStart : newEnd - oldEnd;
        // Trim na borda esquerda com ripple move o próprio clip e tudo à frente.
        next = shiftItemsAfter(next, oldEnd - EPSILON, delta, fps);
        if (p.edge === 'start') {
            const moved = next.items.map((i) => i.id === p.itemId ? shiftItem(i, delta, fps) : i);
            next = { ...next, items: sortItems(moved) };
        }
    }
    return {
        sequence: normalized(replaceTracks(seq, [next]), [track.id]),
        inverse: restoreInverse(ctx, [track.id]),
        changed: [p.itemId],
    };
}
/**
 * Trim pode ESTENDER o item (não só encurtar), então não usa `sliceItem` — que só
 * recorta. Aqui a extensão é permitida até o limite da mídia de origem.
 */
function sliceItemForTrim(item, newStart, newEnd, fps) {
    const duration = newEnd - newStart;
    if (duration < minDuration(fps) - EPSILON)
        return null;
    const next = cloneItem(item);
    const offset = newStart - item.start;
    const speed = Math.abs(item.speed) || 1;
    next.start = quantize(newStart, fps);
    next.duration = quantize(duration, fps);
    const src = item.source;
    if (src) {
        if (item.reverse) {
            const outAtStart = src.out - offset * speed;
            next.source = { assetId: src.assetId, in: outAtStart - duration * speed, out: outAtStart };
        }
        else {
            const inAtStart = src.in + offset * speed;
            next.source = { assetId: src.assetId, in: inAtStart, out: inAtStart + duration * speed };
        }
    }
    if (offset !== 0 && next.keyframes.length > 0) {
        next.keyframes = next.keyframes.map((k) => ({ ...k, time: k.time - offset }));
    }
    next.fadeIn = Math.min(next.fadeIn, next.duration);
    next.fadeOut = Math.min(next.fadeOut, next.duration);
    if (next.caption) {
        next.caption.words = next.caption.words.filter((w) => w.end > next.start && w.start < next.start + next.duration);
    }
    return next;
}
/** Menor `start` possível: limitado pelo material disponível antes do ponto de entrada. */
function mediaLowerBound(item) {
    if (!item.source)
        return 0;
    const speed = Math.abs(item.speed) || 1;
    const available = item.reverse
        ? Number.POSITIVE_INFINITY
        : item.source.in / speed;
    return Math.max(0, item.start - available);
}
/** Maior `end` possível. Sem `assetDuration` conhecida aqui, o limite é permissivo:
 *  a validação definitiva contra a duração real do asset acontece na API. */
function mediaUpperBound(item) {
    if (!item.source)
        return Number.POSITIVE_INFINITY;
    return Number.POSITIVE_INFINITY;
}
function splitItem(ctx, p) {
    const { seq } = ctx;
    const { track, item } = requireItem(seq, p.itemId);
    assertEditable(track);
    const fps = seq.frameRate;
    const time = quantize(p.time, fps);
    const min = minDuration(fps);
    if (lte(time, item.start + min - EPSILON) || gte(time, itemEndOf(item) - min + EPSILON)) {
        throw new CommandError('SPLIT_OUT_OF_BOUNDS', 'Ponto de corte fora do item (ou muito próximo da borda)');
    }
    const left = sliceItemForTrim(item, item.start, time, fps);
    const right = sliceItemForTrim(item, time, itemEndOf(item), fps);
    right.id = p.newItemId ?? ctx.idFactory();
    // Fades pertencem às bordas externas do par.
    left.fadeOut = 0;
    right.fadeIn = 0;
    // Keyframes já foram reancorados por sliceItemForTrim.
    const items = sortItems([
        ...track.items.filter((i) => i.id !== p.itemId),
        left,
        right,
    ]);
    return {
        sequence: normalized(replaceTracks(seq, [{ ...track, items }]), [track.id]),
        inverse: restoreInverse(ctx, [track.id]),
        changed: [left.id, right.id],
    };
}
function duplicateItems(ctx, p) {
    const { seq } = ctx;
    const targets = new Set(p.itemIds);
    const updated = [];
    const affected = [];
    const newIds = [];
    for (const track of seq.tracks) {
        const hits = track.items.filter((i) => targets.has(i.id));
        if (hits.length === 0)
            continue;
        assertEditable(track);
        affected.push(track.id);
        let next = cloneTrack(track);
        for (const hit of hits) {
            const copy = cloneItem(hit);
            copy.id = p.newIds?.[hit.id] ?? ctx.idFactory();
            const offset = p.offset ?? hit.duration;
            const placed = shiftItem(copy, offset, seq.frameRate);
            newIds.push(placed.id);
            next = insertOverwrite(next, placed, seq.frameRate, ctx.idFactory);
        }
        updated.push(next);
    }
    if (updated.length === 0)
        throw new CommandError('ITEM_NOT_FOUND', 'Nada para duplicar');
    return {
        sequence: normalized(replaceTracks(seq, updated), affected),
        inverse: restoreInverse(ctx, affected),
        changed: newIds,
    };
}
// ---------------------------------------------------------------------------
// Operações de range — o motor da IA e da edição por texto
// ---------------------------------------------------------------------------
/**
 * DELETE_RANGE remove [start,end) e fecha o buraco; LIFT_RANGE deixa o buraco.
 *
 * O ripple é aplicado com o MESMO delta em todas as tracks alvo. É isso que mantém
 * áudio, vídeo e legenda sincronizados quando a IA corta uma frase inteira — cada
 * track fechando o próprio buraco de forma independente seria a receita para
 * dessincronizar tudo.
 */
function rangeOp(ctx, p, ripple) {
    const { seq } = ctx;
    const fps = seq.frameRate;
    const start = quantize(Math.max(0, p.start), fps);
    const end = quantize(Math.max(start, p.end), fps);
    if (lte(end, start)) {
        throw new CommandError('EMPTY_RANGE', 'Intervalo vazio');
    }
    const tracks = targetTracks(seq, p.trackIds);
    if (tracks.length === 0)
        throw new CommandError('NO_TARGET_TRACKS', 'Nenhuma track editável');
    const updated = tracks.map((track) => {
        let next = carveRange(track, start, end, fps, ctx.idFactory);
        if (ripple)
            next = shiftItemsAfter(next, end - EPSILON, -(end - start), fps);
        return next;
    });
    const affected = tracks.map((t) => t.id);
    // Marcadores acompanham o ripple.
    let markers = seq.markers;
    if (ripple) {
        markers = seq.markers
            .filter((m) => !(gte(m.time, start) && lt(m.time, end)))
            .map((m) => (gte(m.time, end) ? { ...m, time: m.time - (end - start) } : m));
    }
    const sequence = { ...normalized(replaceTracks(seq, updated), affected), markers };
    return {
        sequence,
        inverse: restoreInverse(ctx, affected, ripple),
        changed: [],
    };
}
function insertGap(ctx, p) {
    const { seq } = ctx;
    const fps = seq.frameRate;
    const time = quantize(Math.max(0, p.time), fps);
    const duration = quantize(p.duration, fps);
    if (lte(duration, 0))
        throw new CommandError('EMPTY_RANGE', 'Duração inválida');
    const tracks = targetTracks(seq, p.trackIds);
    const updated = tracks.map((track) => {
        // Um item que CONTÉM o ponto é dividido para que o espaço abra no meio.
        const containing = track.items.find((i) => gt(time, i.start) && lt(time, itemEndOf(i)));
        let next = track;
        if (containing) {
            const left = sliceItemForTrim(containing, containing.start, time, fps);
            const right = sliceItemForTrim(containing, time, itemEndOf(containing), fps);
            if (left && right) {
                right.id = ctx.idFactory();
                next = {
                    ...track,
                    items: sortItems([...track.items.filter((i) => i.id !== containing.id), left, right]),
                };
            }
        }
        return shiftItemsAfter(next, time, duration, fps);
    });
    const affected = tracks.map((t) => t.id);
    const markers = seq.markers.map((m) => (gte(m.time, time) ? { ...m, time: m.time + duration } : m));
    return {
        sequence: { ...normalized(replaceTracks(seq, updated), affected), markers },
        inverse: restoreInverse(ctx, affected, true),
        changed: [],
    };
}
// ---------------------------------------------------------------------------
// Propriedades
// ---------------------------------------------------------------------------
function setSpeed(ctx, p) {
    const { seq } = ctx;
    const { track, item } = requireItem(seq, p.itemId);
    assertEditable(track);
    const speed = clamp(Math.abs(p.speed), MIN_SPEED, MAX_SPEED);
    const preserve = p.preserve ?? 'duration';
    const next = cloneItem(item);
    next.speed = speed;
    if (p.reverse !== undefined)
        next.reverse = p.reverse;
    if (p.preservePitch !== undefined)
        next.preservePitch = p.preservePitch;
    if (next.source) {
        if (preserve === 'duration') {
            // Mantém a duração na timeline e reenquadra o range de origem.
            const consumed = next.duration * speed;
            next.source = { ...next.source, out: next.source.in + consumed };
        }
        else {
            // Mantém o range de origem e recalcula a duração.
            const consumed = next.source.out - next.source.in;
            next.duration = quantize(consumed / speed, seq.frameRate);
        }
    }
    const items = track.items.map((i) => (i.id === p.itemId ? next : i));
    const updatedTrack = { ...track, items: sortItems(items) };
    // A duração muda: precisa reacomodar vizinhos (overwrite).
    const rebuilt = rebuildWithOverwrite(updatedTrack, next, seq.frameRate, ctx.idFactory);
    return {
        sequence: normalized(replaceTracks(seq, [rebuilt]), [track.id]),
        inverse: restoreInverse(ctx, [track.id]),
        changed: [p.itemId],
    };
}
function rebuildWithOverwrite(track, item, fps, idFactory) {
    const without = { ...track, items: track.items.filter((i) => i.id !== item.id) };
    return insertOverwrite(without, item, fps, idFactory);
}
function setTransform(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const before = { ...item.transform };
        item.transform = { ...item.transform, ...p.transform };
        item.transform.opacity = clamp(item.transform.opacity, 0, 1);
        return () => inv(ctx, 'SET_TRANSFORM', { itemId: p.itemId, transform: before });
    });
}
function setCrop(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const before = { ...item.crop };
        const merged = { ...item.crop, ...p.crop };
        item.crop = {
            top: clamp(merged.top, 0, 0.99),
            right: clamp(merged.right, 0, 0.99),
            bottom: clamp(merged.bottom, 0, 0.99),
            left: clamp(merged.left, 0, 0.99),
        };
        return () => inv(ctx, 'SET_CROP', { itemId: p.itemId, crop: before });
    });
}
function setVolume(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const before = {
            itemId: p.itemId,
            volume: item.volume,
            muted: item.muted,
            fadeIn: item.fadeIn,
            fadeOut: item.fadeOut,
        };
        if (p.volume !== undefined)
            item.volume = clamp(p.volume, 0, 2);
        if (p.muted !== undefined)
            item.muted = p.muted;
        if (p.fadeIn !== undefined)
            item.fadeIn = clamp(p.fadeIn, 0, item.duration);
        if (p.fadeOut !== undefined)
            item.fadeOut = clamp(p.fadeOut, 0, item.duration);
        return () => inv(ctx, 'SET_VOLUME', before);
    });
}
/** Mutação local a um item, sem alterar tempos — inverso analítico exato. */
function propertyPatch(ctx, itemId, mutate) {
    const { seq } = ctx;
    const { track, item } = requireItem(seq, itemId);
    assertEditable(track);
    const draft = cloneItem(item);
    const makeInverse = mutate(draft);
    const items = track.items.map((i) => (i.id === itemId ? draft : i));
    return {
        sequence: replaceTracks(seq, [{ ...track, items }]),
        inverse: makeInverse(),
        changed: [itemId],
    };
}
// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------
function setKeyframes(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const before = item.keyframes.filter((k) => k.property === p.property).map((k) => ({ ...k }));
        item.keyframes = [
            ...item.keyframes.filter((k) => k.property !== p.property),
            ...p.keyframes.map((k) => ({ ...k, property: p.property })),
        ].sort((a, b) => a.time - b.time);
        return () => inv(ctx, 'SET_KEYFRAMES', { itemId: p.itemId, property: p.property, keyframes: before });
    });
}
function addKeyframe(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        // Um keyframe no mesmo tempo e propriedade substitui o anterior.
        item.keyframes = [
            ...item.keyframes.filter((k) => !(k.property === p.keyframe.property && Math.abs(k.time - p.keyframe.time) < EPSILON)),
            { ...p.keyframe },
        ].sort((a, b) => a.time - b.time);
        return () => inv(ctx, 'REMOVE_KEYFRAMES', { itemId: p.itemId, keyframeIds: [p.keyframe.id] });
    });
}
function removeKeyframes(ctx, p) {
    const { item } = requireItem(ctx.seq, p.itemId);
    const removed = item.keyframes.filter((k) => p.keyframeIds.includes(k.id)).map((k) => ({ ...k }));
    return propertyPatch(ctx, p.itemId, (draft) => {
        draft.keyframes = draft.keyframes.filter((k) => !p.keyframeIds.includes(k.id));
        return () => {
            // Restaura em lote: um BATCH de ADD_KEYFRAME.
            const commands = removed.map((k) => inv(ctx, 'ADD_KEYFRAME', { itemId: p.itemId, keyframe: k }));
            return inv(ctx, 'BATCH', { commands });
        };
    });
}
function updateKeyframe(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const existing = item.keyframes.find((k) => k.id === p.keyframeId);
        if (!existing)
            throw new CommandError('KEYFRAME_NOT_FOUND', `Keyframe ${p.keyframeId} não existe`);
        const before = { ...existing };
        item.keyframes = item.keyframes
            .map((k) => (k.id === p.keyframeId ? { ...k, ...p.patch } : k))
            .sort((a, b) => a.time - b.time);
        return () => inv(ctx, 'UPDATE_KEYFRAME', {
            itemId: p.itemId,
            keyframeId: p.keyframeId,
            patch: { time: before.time, value: before.value, easing: before.easing },
        });
    });
}
// ---------------------------------------------------------------------------
// Efeitos
// ---------------------------------------------------------------------------
function addEffect(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        item.effects = [...item.effects, { ...p.effect, params: { ...p.effect.params } }];
        return () => inv(ctx, 'REMOVE_EFFECT', { itemId: p.itemId, effectId: p.effect.id });
    });
}
function removeEffect(ctx, p) {
    const { item } = requireItem(ctx.seq, p.itemId);
    const removed = item.effects.find((e) => e.id === p.effectId);
    if (!removed)
        throw new CommandError('EFFECT_NOT_FOUND', `Efeito ${p.effectId} não existe`);
    const snapshot = { ...removed, params: { ...removed.params } };
    return propertyPatch(ctx, p.itemId, (draft) => {
        draft.effects = draft.effects.filter((e) => e.id !== p.effectId);
        return () => inv(ctx, 'ADD_EFFECT', { itemId: p.itemId, effect: snapshot });
    });
}
function updateEffect(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        const existing = item.effects.find((e) => e.id === p.effectId);
        if (!existing)
            throw new CommandError('EFFECT_NOT_FOUND', `Efeito ${p.effectId} não existe`);
        const before = { enabled: existing.enabled, params: { ...existing.params } };
        item.effects = item.effects.map((e) => e.id === p.effectId
            ? { ...e, ...p.patch, params: { ...e.params, ...(p.patch.params ?? {}) } }
            : e);
        return () => inv(ctx, 'UPDATE_EFFECT', { itemId: p.itemId, effectId: p.effectId, patch: before });
    });
}
// ---------------------------------------------------------------------------
// Texto e legendas
// ---------------------------------------------------------------------------
function addText(ctx, p) {
    return addItem(ctx, { trackId: p.trackId, item: p.item, mode: 'overwrite' });
}
function updateText(ctx, p) {
    return propertyPatch(ctx, p.itemId, (item) => {
        if (!item.text)
            throw new CommandError('NOT_TEXT_ITEM', `Item ${p.itemId} não é texto`);
        const before = {};
        for (const key of Object.keys(p.patch)) {
            before[key] = item.text[key];
        }
        item.text = { ...item.text, ...p.patch };
        return () => inv(ctx, 'UPDATE_TEXT', { itemId: p.itemId, patch: before });
    });
}
function addCaptions(ctx, p) {
    const { seq } = ctx;
    const track = requireTrack(seq, p.trackId);
    assertEditable(track);
    const items = p.items.map((i) => quantizeItem(cloneItem(i), seq));
    const base = p.replace ? { ...track, items: [] } : track;
    let next = base;
    for (const item of items) {
        next = insertOverwrite(next, item, seq.frameRate, ctx.idFactory);
    }
    return {
        sequence: normalized(replaceTracks(seq, [next]), [track.id]),
        inverse: restoreInverse(ctx, [track.id]),
        changed: items.map((i) => i.id),
    };
}
function updateCaptionStyle(ctx, p) {
    const { seq } = ctx;
    const byTrack = new Map();
    const restore = [];
    for (const itemId of p.itemIds) {
        const { track } = requireItem(seq, itemId);
        assertEditable(track);
        restore.push(track.id);
        const current = byTrack.get(track.id) ?? track;
        byTrack.set(track.id, {
            ...current,
            items: current.items.map((i) => {
                if (i.id !== itemId || !i.caption)
                    return i;
                const draft = cloneItem(i);
                draft.caption = { ...draft.caption, ...p.patch };
                return draft;
            }),
        });
    }
    const affected = [...new Set(restore)];
    return {
        sequence: replaceTracks(seq, [...byTrack.values()]),
        inverse: restoreInverse(ctx, affected),
        changed: p.itemIds,
    };
}
function setCaptionWords(ctx, p) {
    const { item } = requireItem(ctx.seq, p.itemId);
    const before = item.caption?.words.map((w) => ({ ...w })) ?? [];
    return propertyPatch(ctx, p.itemId, (draft) => {
        if (!draft.caption)
            throw new CommandError('NOT_CAPTION_ITEM', `Item ${p.itemId} não é legenda`);
        draft.caption.words = p.words.map((w) => ({ ...w }));
        return () => inv(ctx, 'SET_CAPTION_WORDS', { itemId: p.itemId, words: before });
    });
}
// ---------------------------------------------------------------------------
// Marcadores, grupos, sequência
// ---------------------------------------------------------------------------
function addMarker(ctx, p) {
    const { seq } = ctx;
    return {
        sequence: { ...seq, markers: [...seq.markers, { ...p.marker }].sort((a, b) => a.time - b.time) },
        inverse: inv(ctx, 'REMOVE_MARKER', { markerId: p.marker.id }),
        changed: [],
    };
}
function removeMarker(ctx, p) {
    const { seq } = ctx;
    const marker = seq.markers.find((m) => m.id === p.markerId);
    if (!marker)
        throw new CommandError('MARKER_NOT_FOUND', `Marcador ${p.markerId} não existe`);
    return {
        sequence: { ...seq, markers: seq.markers.filter((m) => m.id !== p.markerId) },
        inverse: inv(ctx, 'ADD_MARKER', { marker: { ...marker } }),
        changed: [],
    };
}
function updateMarker(ctx, p) {
    const { seq } = ctx;
    const marker = seq.markers.find((m) => m.id === p.markerId);
    if (!marker)
        throw new CommandError('MARKER_NOT_FOUND', `Marcador ${p.markerId} não existe`);
    const before = {};
    for (const key of Object.keys(p.patch)) {
        ;
        before[key] = marker[key];
    }
    return {
        sequence: {
            ...seq,
            markers: seq.markers
                .map((m) => (m.id === p.markerId ? { ...m, ...p.patch } : m))
                .sort((a, b) => a.time - b.time),
        },
        inverse: inv(ctx, 'UPDATE_MARKER', { markerId: p.markerId, patch: before }),
        changed: [],
    };
}
function groupItems(ctx, p) {
    const { seq } = ctx;
    const targets = new Set(p.itemIds);
    const updated = seq.tracks
        .filter((t) => t.items.some((i) => targets.has(i.id)))
        .map((t) => ({
        ...t,
        items: t.items.map((i) => (targets.has(i.id) ? { ...i, groupId: p.groupId } : i)),
    }));
    return {
        sequence: replaceTracks(seq, updated),
        inverse: inv(ctx, 'UNGROUP_ITEMS', { groupId: p.groupId }),
        changed: p.itemIds,
    };
}
function ungroupItems(ctx, p) {
    const { seq } = ctx;
    const ids = [];
    const updated = seq.tracks
        .filter((t) => t.items.some((i) => i.groupId === p.groupId))
        .map((t) => ({
        ...t,
        items: t.items.map((i) => {
            if (i.groupId !== p.groupId)
                return i;
            ids.push(i.id);
            const { groupId, ...rest } = i;
            return rest;
        }),
    }));
    return {
        sequence: replaceTracks(seq, updated),
        inverse: inv(ctx, 'GROUP_ITEMS', { itemIds: ids, groupId: p.groupId }),
        changed: ids,
    };
}
function setSequenceSettings(ctx, p) {
    const { seq } = ctx;
    const before = {};
    for (const key of Object.keys(p)) {
        ;
        before[key] = seq[key];
    }
    return {
        sequence: { ...seq, ...p },
        inverse: inv(ctx, 'SET_SEQUENCE_SETTINGS', before),
        changed: [],
    };
}
function restoreTracks(ctx, p) {
    const { seq } = ctx;
    const restoreIds = p.tracks.map((t) => t.id);
    const removeIds = new Set(p.removeTrackIds ?? []);
    const previous = snapshotTracks(seq, restoreIds);
    const byId = new Map(p.tracks.map((t) => [t.id, cloneTrack(t)]));
    const existing = seq.tracks
        .filter((t) => !removeIds.has(t.id))
        .map((t) => byId.get(t.id) ?? t);
    const existingIds = new Set(existing.map((t) => t.id));
    const added = p.tracks.filter((t) => !existingIds.has(t.id)).map(cloneTrack);
    const inversePayload = {
        tracks: previous,
        removeTrackIds: added.map((t) => t.id),
    };
    let markers = seq.markers;
    if (p.markers) {
        inversePayload.markers = seq.markers.map((m) => ({ ...m }));
        markers = p.markers.map((m) => ({ ...m }));
    }
    return {
        sequence: { ...seq, tracks: sortTracks([...existing, ...added]), markers },
        inverse: inv(ctx, 'RESTORE_TRACKS', inversePayload),
        changed: p.tracks.flatMap((t) => t.items.map((i) => i.id)),
    };
}
/**
 * BATCH: N comandos com atomicidade e UM único passo de undo.
 *
 * É o que torna "31 alterações do Auto Edit" reversíveis com um Ctrl+Z, sem perder a
 * identidade individual de cada alteração (preservada pelo `changeId`).
 */
function batch(ctx, p) {
    let current = ctx.seq;
    const inverses = [];
    const changed = new Set();
    for (const child of p.commands) {
        const r = dispatchScoped({ ...ctx, seq: current, command: child });
        current = r.sequence;
        inverses.unshift(r.inverse);
        r.changed.forEach((id) => changed.add(id));
    }
    return {
        sequence: current,
        inverse: inv(ctx, 'BATCH', { commands: inverses }),
        changed: [...changed],
    };
}
// ---------------------------------------------------------------------------
function quantizeItem(item, seq) {
    const fps = seq.frameRate;
    return {
        ...item,
        start: quantize(Math.max(0, item.start), fps),
        duration: quantize(item.duration, fps),
    };
}
export { sequenceDuration };
//# sourceMappingURL=apply.js.map